import { useQuery } from "@tanstack/react-query";
import { BookOpen, PackagePlus, Plus, Upload } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  SearchInput,
  SkeletonRows,
} from "@/components/ui";
import { useDebounced } from "@/lib/useDebounced";
import { inventoryRepository } from "@/repositories/catalogueRepository";
import { useAuthStore } from "@/stores/authStore";

import { ImportWizard } from "./ImportWizard";
import { MasterCataloguePanel } from "./MasterCataloguePanel";
import { ProductTable } from "./ProductTable";
import { StockFilters } from "./StockFilters";
import {
  KpiRow,
  LowStockPanel,
  MovementsPanel,
  StatusDoughnut,
} from "./panels";
import { useCategories } from "./useCatalogueData";
import { useScopeKey } from "./useScopeKey";

const PAGE_SIZE = 20;

/**
 * Inventory.
 *
 * The screen from the supplied design: four KPI cards, a filtered product table
 * carrying stock and status, and three panels underneath. Every number is read
 * from the stock ledger — an unstocked shop shows zeroes and empty panels rather
 * than the design's illustrative figures.
 *
 * The overview is one request, not four. The cards, the doughnut and both panels
 * are always read together, and four round trips on a township 3G connection is
 * most of a second of watching skeletons.
 */
export function InventoryPage() {
  const can = useAuthStore((s) => s.can);
  const canImport = can("products.import");
  const canCreate = can("products.create");

  const [panel, setPanel] = useState<"none" | "import" | "master">("none");
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const debouncedTerm = useDebounced(term, 250);
  // Part of every key below: the scope travels in a header, so without it two
  // different counters look like the same request to the cache.
  const scope = useScopeKey();

  const overview = useQuery({
    queryKey: ["inventory", "overview", scope],
    queryFn: () => inventoryRepository.overview(),
  });

  const products = useQuery({
    queryKey: [
      "inventory",
      "products",
      scope,
      debouncedTerm,
      category,
      status,
      page,
    ],
    queryFn: () =>
      inventoryRepository.products({
        q: debouncedTerm,
        category,
        status,
        page,
      }),
    // Keeps the previous page on screen while the next loads, so paging does not
    // collapse the table to a skeleton and jump the scroll position.
    placeholderData: (previous) => previous,
  });

  const categories = useCategories();

  if (panel === "import") {
    return <ImportWizard onDone={() => setPanel("none")} />;
  }

  /** Any filter change returns to page one; page 4 of a new filter is not a place. */
  function refine(apply: () => void) {
    apply();
    setPage(1);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap justify-end gap-2">
        {canCreate && (
          <Button
            variant="secondary"
            icon={<BookOpen className="size-4" aria-hidden />}
            onClick={() => setPanel(panel === "master" ? "none" : "master")}
          >
            Add from the shared list
          </Button>
        )}
        {canImport && (
          <Button
            variant="secondary"
            icon={<Upload className="size-4" aria-hidden />}
            onClick={() => setPanel("import")}
          >
            Import products
          </Button>
        )}
        {canCreate && (
          <Button
            variant="primary"
            icon={<Plus className="size-4" aria-hidden />}
            disabled
          >
            Add product
          </Button>
        )}
      </div>

      {panel === "master" && (
        <MasterCataloguePanel onClose={() => setPanel("none")} />
      )}

      {/* ── KPI cards ───────────────────────────────────────────────────────── */}
      {overview.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((slot) => (
            <Card key={slot}>
              <SkeletonRows rows={2} />
            </Card>
          ))}
        </div>
      ) : overview.isError ? (
        <ErrorState
          title="Stock figures could not be loaded"
          onRetry={() => void overview.refetch()}
        />
      ) : (
        overview.data && <KpiRow summary={overview.data.summary} />
      )}

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={term}
          onChange={(event) => refine(() => setTerm(event.target.value))}
          placeholder="Search products by name, barcode or nickname"
          containerClassName="w-full flex-1 sm:w-auto sm:min-w-[16rem]"
        />
        <StockFilters
          categories={categories.data ?? []}
          category={category}
          status={status}
          onCategory={(value) => refine(() => setCategory(value))}
          onStatus={(value) => refine(() => setStatus(value))}
        />
      </div>

      {/* ── Product list ────────────────────────────────────────────────────── */}
      <Card flush className="p-5">
        <h2 className="text-base font-semibold text-ink-900">Product list</h2>

        <div className="mt-3">
          {products.isLoading ? (
            <SkeletonRows rows={8} />
          ) : products.isError ? (
            <ErrorState
              title="The product list could not be loaded"
              onRetry={() => void products.refetch()}
            />
          ) : products.data && products.data.results.length > 0 ? (
            <ProductTable
              products={products.data.results}
              total={products.data.count}
              page={page}
              pageSize={PAGE_SIZE}
              onPage={setPage}
            />
          ) : (
            <EmptyCatalogue
              filtered={
                debouncedTerm !== "" || category !== "" || status !== ""
              }
              canImport={canImport}
              onImport={() => setPanel("import")}
              onBrowse={() => setPanel("master")}
            />
          )}
        </div>
      </Card>

      {/* ── The three panels ────────────────────────────────────────────────── */}
      {overview.data && (
        <div className="grid gap-4 xl:grid-cols-3">
          <StatusDoughnut breakdown={overview.data.status_breakdown} />
          <LowStockPanel
            items={overview.data.low_stock}
            onView={() => refine(() => setStatus("LOW_STOCK"))}
          />
          <MovementsPanel
            movements={overview.data.recent_movements}
            onView={() => refine(() => setStatus(""))}
          />
        </div>
      )}
    </div>
  );
}

function EmptyCatalogue({
  filtered,
  canImport,
  onImport,
  onBrowse,
}: {
  filtered: boolean;
  canImport: boolean;
  onImport: () => void;
  onBrowse: () => void;
}) {
  if (filtered) {
    return (
      <EmptyState
        icon={PackagePlus}
        title="Nothing matched"
        description="Try fewer words, or clear the filters."
      />
    );
  }

  return (
    <EmptyState
      icon={PackagePlus}
      title="No products yet"
      description={
        canImport
          ? "Add products from the shared list, or upload a spreadsheet you already have."
          : "Ask a manager to add products to this business."
      }
      action={
        canImport && (
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="primary" onClick={onImport}>
              Import from a file
            </Button>
            <Button variant="secondary" onClick={onBrowse}>
              Add from the shared list
            </Button>
          </div>
        )
      }
    />
  );
}
